import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Tag,
  TrendingUp,
  Clock,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import Card from '../common/Card';
import Table from '../common/Table';
import StatusIndicator, { getStockLevelColor } from '../common/StatusIndicator';
import PriceHistory from '../Charts/PriceHistory';
import { useApi } from '../../hooks/useApi';
import { getItemHistory, getTransactions, getStock } from '../../api/client';
import { format } from 'date-fns';

const ZONES = [
  'RASMUSTAV', 'Clear_Sky', 'SIDOROVICH', 'Bradislav',
  'Wirpmenistan', 'Briggosia', 'GREENMOUNTAIN',
];

const txColumns = [
  {
    key: 'type',
    label: 'Type',
    render: (v) => <StatusIndicator type="transaction" value={v} />,
  },
  { key: 'zone', label: 'Zone', render: (v) => <span className="text-xs">{v}</span> },
  {
    key: 'quantity',
    label: 'Qty',
    mono: true,
    align: 'right',
    render: (v) => <span className="font-mono text-xs">{v}</span>,
  },
  {
    key: 'price',
    label: 'Price',
    mono: true,
    align: 'right',
    render: (v) => <span className="font-mono text-accent-green text-xs">₽{(v || 0).toLocaleString()}</span>,
  },
  {
    key: 'timestamp',
    label: 'Time',
    render: (v) => (
      <span className="text-text-muted text-xs font-mono">
        {v ? format(new Date(v), 'MMM dd HH:mm') : '—'}
      </span>
    ),
  },
];

function StockByZoneTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="glass-card p-3 text-xs">
      <p className="text-text-primary font-medium mb-1">{data.zone}</p>
      <p className="font-mono text-accent-green">
        Stock: {data.quantity?.toLocaleString()} / {data.maxQuantity?.toLocaleString()}
      </p>
    </div>
  );
}

export default function ItemDetail() {
  const { classname } = useParams();
  const navigate = useNavigate();

  const { data: stockData, loading: stockLoading } = useApi(
    getStock,
    { search: classname }
  );
  const { data: txData, loading: txLoading } = useApi(
    getTransactions,
    { classname, limit: 50 }
  );

  const itemInfo = useMemo(() => {
    if (!stockData) return null;
    const items = Array.isArray(stockData) ? stockData : stockData.items || stockData.data || [];
    const matching = items.filter(
      (i) => (i.classname || i.name) === classname
    );
    if (matching.length === 0 && items.length > 0) return items[0];
    return matching[0] || null;
  }, [stockData, classname]);

  const stockByZone = useMemo(() => {
    if (!stockData) return [];
    const items = Array.isArray(stockData) ? stockData : stockData.items || stockData.data || [];
    const matching = items.filter(
      (i) => (i.classname || i.name) === classname
    );

    if (matching.length > 1) {
      return matching.map((i) => ({
        zone: i.zone || 'Unknown',
        quantity: i.quantity || 0,
        maxQuantity: i.maxQuantity || i.max_quantity || 100,
        percent: i.percent ?? (i.quantity / (i.maxQuantity || i.max_quantity || 1)) * 100,
      }));
    }

    // Generate placeholder zone data
    return ZONES.map((zone) => {
      const found = matching.find((i) => i.zone === zone);
      return {
        zone,
        quantity: found?.quantity ?? 0,
        maxQuantity: found?.maxQuantity ?? found?.max_quantity ?? 100,
        percent: found?.percent ?? 0,
      };
    });
  }, [stockData, classname]);

  const transactions = useMemo(() => {
    if (!txData) return [];
    return Array.isArray(txData) ? txData : txData.data || [];
  }, [txData]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/items')}
          className="p-2 rounded bg-terminal-surface border border-terminal-border hover:border-text-muted transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-text-secondary" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <Package className="w-5 h-5 text-accent-green" />
            {classname}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            {itemInfo?.category && (
              <span className="flex items-center gap-1 text-xs font-mono text-text-muted">
                <Tag className="w-3 h-3" />
                {itemInfo.category}
              </span>
            )}
            {itemInfo?.zone && (
              <span className="text-xs text-text-muted font-mono">
                Zone: {itemInfo.zone}
              </span>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-[10px] text-text-muted uppercase">Buy Price</p>
            <p className="text-lg font-mono font-bold text-accent-green">
              ₽{(itemInfo?.buyPrice || itemInfo?.buy_price || 0).toLocaleString()}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-text-muted uppercase">Sell Price</p>
            <p className="text-lg font-mono font-bold text-accent-red">
              ₽{(itemInfo?.sellPrice || itemInfo?.sell_price || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Stock by Zone + Price History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Stock by Zone" icon={Package}>
          {stockLoading ? (
            <div className="skeleton h-[250px] rounded" />
          ) : stockByZone.length === 0 ? (
            <div className="flex items-center justify-center h-[250px] text-text-muted text-sm">
              No stock data
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stockByZone} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="zone"
                  tick={{ fill: '#94a3b8', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  axisLine={{ stroke: '#1e293b' }}
                  tickLine={false}
                  angle={-20}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  axisLine={{ stroke: '#1e293b' }}
                  tickLine={false}
                />
                <Tooltip content={<StockByZoneTooltip />} cursor={{ fill: 'rgba(30, 41, 59, 0.3)' }} />
                <Bar dataKey="quantity" radius={[4, 4, 0, 0]}>
                  {stockByZone.map((entry, idx) => (
                    <Cell key={idx} fill={getStockLevelColor(entry.percent)} fillOpacity={0.8} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <PriceHistory classname={classname} />
      </div>

      {/* Transaction History */}
      <Card title="Transaction History" icon={Clock}>
        <Table
          columns={txColumns}
          data={transactions}
          loading={txLoading}
          pageSize={15}
          emptyMessage="No transactions found for this item"
        />
      </Card>
    </div>
  );
}
